type Fetcher<Params extends unknown[], Result> = (
    ...params: Params
) => Promise<Result>;
type Hasher<Params extends unknown[]> = (params: Params) => string;

export function cacheResponse<Params extends unknown[], Result>(
    fetcher: Fetcher<Params, Result>,
    hasher: Hasher<Params>
): Fetcher<Params, Result> {
    const cache: Map<string, Result> = new Map();
    return async function cachedFetcher(...params: Params) {
        const hash = hasher(params);
        if (cache.has(hash)) {
            return cache.get(hash) as Result;
        }
        const result = await fetcher(...params);
        cache.set(hash, result);

        return result;
    };
}

export function cacheRequest<Params extends unknown[], Result>(
    fetcher: Fetcher<Params, Result>,
    hasher: Hasher<Params>
): Fetcher<Params, Result> {
    const cache: Map<string, Promise<Result>> = new Map();
    return async function cachedRequest(...params: Params) {
        const hash = hasher(params);
        const cachedPromise = cache.get(hash);
        if (cachedPromise) {
            return cachedPromise;
        }
        try {
            const promise = fetcher(...params);
            cache.set(hash, promise);
            return promise;
        } finally {
            cache.delete(hash);
        }
    };
}
